import { Fragment, ReactNode } from 'react';
import Link from 'next/link';

type IndexedTokenType = { token: SpecialTokenType; idx: number };
type IsolatedStringType = {
    before: string;
    inside: string;
    after: string;
    token: SpecialTokenType;
    props: object;
};
type SpecialTokenType = {
    open: string;
    close: string;
    component: JSX.ElementType;
    // Add more strings when needed
    id: 'link';
};
export type PropsObjectByTokenType = Record<SpecialTokenType['id'], object>;

const LINK_TOKEN: SpecialTokenType = {
    open: '{linkOpen}',
    close: '{linkClose}',
    component: Link,
    id: 'link',
};

export const specialTokens: SpecialTokenType[] = [LINK_TOKEN];

const divideStringWithToken = (token: string, text: string): string[] => {
    return [
        text.substring(0, text.indexOf(token)),
        text.substring(text.indexOf(token) + token.length),
    ];
};

const isolateSingleTokenPair = (
    token: SpecialTokenType,
    text: string,
    props: object,
): IsolatedStringType => {
    const [beforeOpenToken, afterOpenToken] = divideStringWithToken(token.open, text);
    const [beforeCloseToken, afterCloseToken] = divideStringWithToken(token.close, afterOpenToken);

    const nestedToken = specialTokens.find((specialToken) =>
        beforeCloseToken.includes(specialToken.open),
    );
    if (nestedToken) {
        throw Error(
            `Nested tokens are not supported: found ${nestedToken.open} while looking inside ${token.open}`,
        );
    }

    return {
        before: beforeOpenToken,
        inside: beforeCloseToken,
        after: afterCloseToken,
        token,
        props,
    };
};

const includedSpecialTokens = (text: string): IndexedTokenType[] => {
    const includedTokens = [];
    for (const token of specialTokens) {
        if (text.includes(token.open) || text.includes(token.close)) {
            const tokenOpenCount = text.split(token.open).length - 1;
            const tokenCloseCount = text.split(token.close).length - 1;
            if (tokenOpenCount !== tokenCloseCount) {
                throw Error(`Token ${token.open} is not closed properly`);
            }
            if (tokenOpenCount > 1) {
                throw Error(
                    `Only one token per type is supported per key. Found ${tokenOpenCount} ${token.open}`,
                );
            }
            includedTokens.push({
                token,
                idx: text.indexOf(token.open),
            });
        }
    }
    includedTokens.sort((a, b) => a.idx - b.idx);
    return includedTokens;
};

export const parseTokens = (
    text: string,
    propsPerTokenType?: PropsObjectByTokenType,
): ReactNode[] => {
    const includedTokensInfo = includedSpecialTokens(text);
    if (includedTokensInfo.length === 0) {
        return [<Fragment key={text}>{text}</Fragment>];
    }

    const nodes: IsolatedStringType[] = [];
    includedTokensInfo.forEach((tokenInfo, index) => {
        const tokenPair = isolateSingleTokenPair(
            tokenInfo.token,
            text.substring(index === 0 ? 0 : tokenInfo.idx),
            propsPerTokenType ? propsPerTokenType?.[tokenInfo.token.id] : {},
        );
        nodes.push(tokenPair);
    });

    return nodes.map((node, index) => {
        const Component = node.token.component;
        return (
            <Fragment key={index}>
                {node.before}
                <Component {...node.props}>{node.inside}</Component>
                {index === nodes.length - 1 && node.after}
            </Fragment>
        );
    });
};
